import type { CSSProperties } from 'react'

import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'

import { typography } from '~/constants/typography'

/** DS `Select` の `classNamePrefix`。 menu は body portal されるので、この prefix で CSS を足す。 */
export const DS_SELECT_CLASS_PREFIX = 'eb-ds-select'

/**
 * DS Select の menu 内で「クリックできない行」に当てる見た目。
 *
 * 対象は 2 種:
 * - グループ見出し (`<optgroup>` 相当。 option ではなく選べない)
 * - `disabled` な選択肢 (native `<option disabled>` 相当)
 *
 * DS の `Select` には「選べる option と選べない行が混在する」recipe が無い。
 * 近い DS Menu の group label は 12px / 700 / `Text.highEmphasis` で、
 * Select の option 列 (14px / 400) とは別物。ここは Select の option と同じ
 * 14px を保ち、太さだけ `typography.label` (md / 600) 、色だけ一段落とす
 * `Text.midEmphasis`。
 *
 * **なぜ `styles.option` / `chakraStyles` ではないか**
 * - `chakraStyles` を渡すと DS 側の control / menuList / option が丸ごと消える
 * - option / groupHeading は chakra-react-select が `chakraStyles` だけを見る
 *   (`styles` は menuPortal 以外に効かない)
 * そのため `classNamePrefix` + stylesheet で上書く。
 *
 * 適用口は [[DsSelect]] (およびそれを包む [[FormSelect]])。 DS `Select` を
 * 直書きする画面は同じ prefix を付け、本 module を import して stylesheet を載せる。
 */
export const dsSelectNonInteractiveItemStyle: Pick<
  CSSProperties,
  'fontWeight' | 'color' | 'textTransform'
> = {
  fontWeight: typography.label.fontWeight,
  color: LightModeSemanticColors.Text.midEmphasis.value,
  textTransform: 'none',
}

const STYLE_ID = 'eb-ds-select-noninteractive-style'

const stylesheet = `
.${DS_SELECT_CLASS_PREFIX}__group-heading,
.${DS_SELECT_CLASS_PREFIX}__option--is-disabled {
  font-weight: ${String(dsSelectNonInteractiveItemStyle.fontWeight)} !important;
  color: ${String(dsSelectNonInteractiveItemStyle.color)} !important;
  text-transform: ${String(dsSelectNonInteractiveItemStyle.textTransform)} !important;
}
.${DS_SELECT_CLASS_PREFIX}__group-heading:empty {
  padding: 0 !important;
  height: 0;
  margin-block: 4px;
  border-top: 1px solid ${LightModeSemanticColors.Border.lowEmphasis.value};
}
`

/** menu が body portal されても効くよう、 document に 1 回だけ style を足す。 */
export const ensureDsSelectNonInteractiveStyles = (): void => {
  if (typeof document === 'undefined') return
  if (document.getElementById(STYLE_ID) !== null) return
  const el = document.createElement('style')
  el.id = STYLE_ID
  el.textContent = stylesheet
  document.head.appendChild(el)
}
